import type { OrganizationInvite } from '@reflex/shared';

/**
 * One create/resend response, rendered as a row in the share view.
 * `reissued` is server-reported: an existing invite's token was rotated,
 * so any previously shared link for it is dead.
 */
export type InviteShare = {
  invite: OrganizationInvite;
  redeemUrl: string;
  emailSent: boolean;
  reissued: boolean;
};

/**
 * Title + description for whichever surface is showing the links. The wording
 * depends on whether the deployment actually emailed the invites: when it did,
 * the link is a convenience; when it did not (email is off, the self-hosted
 * default), the link is the only way the invitee ever gets in.
 */
export function shareDialogCopy(shares: InviteShare[]): { title: string; description: string } {
  const multiple = shares.length > 1;
  const emailed = shares.filter((share) => share.emailSent).length;

  if (emailed === 0) {
    return {
      title: multiple ? 'Share invite links' : 'Share invite link',
      description: multiple
        ? 'Send each link to its recipient. The links are shown only once, so copy them now.'
        : `Send this link to ${shares[0].invite.email}. It is shown only once, so copy it now.`,
    };
  }
  if (emailed === shares.length) {
    return {
      title: multiple ? 'Invites sent' : 'Invite sent',
      description: multiple
        ? 'We emailed each invite. Ask recipients to check their inbox, including spam. You can also copy the links below.'
        : `We emailed the invite to ${shares[0].invite.email}. Ask them to check their inbox, including spam. You can also copy the link below.`,
    };
  }
  return {
    title: 'Invites sent',
    description:
      'We emailed some of these invites. Copy the link for anyone we could not email. The links are shown only once.',
  };
}
